import { useContext, useRef, useState } from "react";
import { AppContext } from "../../../contexts/AppContext";
import { Colour } from "../../Variables";
import { Card } from "../Shared/Card";
import { ErrorsDisplay } from "../Shared/Error";
import { RequiredHelper } from "../Shared/RequiredHelper";
import { LoadingContent } from "../Shared/LoadingContent";
import { ValidatedStringFormInput } from "./ValidatedStringFormInput";
import toast from "react-hot-toast";
import { API_IntranetQuickApply, IntranetPositionDetailsModel, IntranetPositionPostModel } from "../../../apis/ApisPositions";
import { RequiredIndicator } from "../Shared/RequiredIndicator";

export type InternalApplicationApplyFormProps = {
    baseState: IntranetPositionDetailsModel,
    id: string,
}

export function InternalApplicationApplyForm(props: InternalApplicationApplyFormProps) {
    const app = useContext(AppContext);
    const [errors, setErrors] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [data, setData] = useState<IntranetPositionPostModel>({
            Title: '',
            Forenames: '',
            CVFileName: '',
            EmailAddress: '',
            PassedProbationPeriod: '',
            CurrentManagerAware: null,
            Surname: '',
            CurrentJobTitle: '',
            CoverLetterFileName: '',
            DesiredSalary: '',
            CurrentSalary: '',
    });
    const [cvFile, setCvFile] = useState<File>();
    const [coverLetterFile, setCoverLetteFile] = useState<File>();
    const inputRefPassedProbationPeriod = useRef<HTMLSelectElement>(null);

    const validate = () => {
        let errors: string[] = [];
        if (props.baseState.Intranet_Title && props.baseState.Intranet_Title_Required && (data?.Title ?? "").length == 0) {
            errors = ([...errors, "Please provide a title"]);
        }
        if ((data?.Forenames ?? "").length == 0) {
            errors = ([...errors, "Please provide a first name"]);
        }
        if ((data?.Surname ?? "").length == 0) {
            errors = ([...errors, "Please provide a last name"]);
        }
        if (props.baseState.IntranetShortForm_CurrentManagerAware && props.baseState.Intranet_CurrentManagerAware_Required && !data?.CurrentManagerAware) {
            errors = ([...errors, "Your current manager must be aware of your application before you may submit"]);
        }
        if ((data?.EmailAddress ?? "").length == 0) {
            errors = ([...errors, "Please provide an email address"]);
        }
        else if (!data?.EmailAddress?.includes("@")) {
            errors = ([...errors, "Please provide a valid email address"]);
        }
        if (cvFile == null) {
            errors = ([...errors, "Please upload a CV"]);
        }
        if (props.baseState.Intranet_CurrentSalary && props.baseState.Intranet_CurrentSalary_Required && (data?.CurrentSalary ?? "").length == 0) {
            errors = ([...errors, "Please provide your current salary"]);
        }
        if (props.baseState.IntranetShortForm_CurrentJobTitle && props.baseState.Intranet_CurrentJobTitle_Required && (data?.CurrentJobTitle ?? "").length == 0) {
            errors = ([...errors, "Please provide your current job title"]);
        }
        if (props.baseState.Intranet_DesiredSalary && props.baseState.Intranet_DesiredSalary_Required && (data?.DesiredSalary ?? "").length == 0) {
            errors = ([...errors, "Please provide your desired salary"]);
        }
        if (props.baseState.Intranet_PassedProbationPeriod && props.baseState.Intranet_PassedProbationPeriod_Required && data?.PassedProbationPeriod == null) {
            errors = ([...errors, "Please provide if you have passed your current probation period"]);
            inputRefPassedProbationPeriod?.current?.classList.remove("is-valid");
            inputRefPassedProbationPeriod?.current?.classList.add("is-invalid");
        }
        else if (props.baseState.Intranet_PassedProbationPeriod && props.baseState.Intranet_PassedProbationPeriod_Required) {
            inputRefPassedProbationPeriod?.current?.classList.add("is-valid");
            inputRefPassedProbationPeriod?.current?.classList.remove("is-invalid");
        }
        setErrors(errors);
        return errors.length == 0;
    }

    const saveDetails = async () => {
        try {
            setSubmitting(true);
            let isValid = validate();
            if (isValid) {
                const formData = new FormData();
                formData.append('File_CV', cvFile);
                formData.append('Forenames', data.Forenames);
                formData.append('Surname', data.Surname);
                formData.append('CVFileName', data.CVFileName);
                formData.append('EmailAddress', data.EmailAddress);

                if (props?.baseState?.Intranet_CoverLetterOption)
                    formData.append('CoverLetterFileName', data.CoverLetterFileName);

                if (props?.baseState?.IntranetShortForm_CurrentJobTitle)
                    formData.append('CurrentJobTitle', data.CurrentJobTitle);

                if (props?.baseState?.Intranet_CurrentSalary)
                    formData.append('CurrentSalary', data.CurrentSalary);

                if (props?.baseState?.Intranet_DesiredSalary)
                    formData.append('DesiredSalary', data.DesiredSalary);

                if (props?.baseState?.Intranet_PassedProbationPeriod)
                    formData.append('PassedProbationPeriod', data.PassedProbationPeriod);

                if(props?.baseState?.Intranet_Title)
                    formData.append('Title', data.Title);

                if (data.CurrentManagerAware != null)
                    formData.append('CurrentManagerAware', data.CurrentManagerAware ? "1" : "0");

                if (props?.baseState?.Intranet_CoverLetterOption)
                    formData.append('File_CL', coverLetterFile);

                var res = await API_IntranetQuickApply(formData, props.id, app.token);
                if (res.Success) {
                    setErrors([]);
                    setSubmitted(true);
                    toast.success("Application submitted");
                }
                else {
                    setErrors(res.Errors);
                }
            }
            else {
                toast.error("Validation issues");
            }
        }
        catch {
            toast.error("Error");
        }
        finally {
            setSubmitting(false);
        }

        setSubmitting(false);
    }

    function updater(state: any, action: string) {
        switch (action) {
            case 'Title': {
                setData({ ...data, Title: state });
                break;
            } case 'Forenames': {
                setData({ ...data, Forenames: state });
                return;
            } case 'Surname': {
                setData({ ...data, Surname: state });
                return;
            } case 'EmailAddress': {
                setData({ ...data, EmailAddress: state });
                return;
            } case 'CurrentManagerAware': {
                setData({ ...data, CurrentManagerAware: state });
                return;
            } case 'CV': {
                setCvFile(state);
                return;
            } case 'CVFileName': {
                setData({ ...data, CVFileName: state });
                return;
            }
            case 'CoverLetter': {
                setCoverLetteFile(state);
                return;
            } case 'CoverLetterFileName': {
                setData({ ...data, CoverLetterFileName: state });
                return;
            } case 'CurrentJobTitle': {
                setData({ ...data, CurrentJobTitle: state });
                return;
            } case 'CurrentSalary': {
                setData({ ...data, CurrentSalary: state });
                return;
            } case 'DesiredSalary': {
                setData({ ...data, DesiredSalary: state });
                return;
            } case 'PassedProbationPeriod': {
                setData({ ...data, PassedProbationPeriod: state });
                return;
            }
        }
    }
    const inputCurrentManagerAware = useRef<HTMLInputElement>(null);
    const cvFileRef = useRef<HTMLInputElement>();
    const coverLetterFileRef = useRef<HTMLInputElement>();
    return (
        <>
            <Card headerClasses="mb-3" headerTheme={Colour.Secondary} title="Application">
                {!submitted &&
                    <LoadingContent loading={app.loading || props.baseState == null} classes={"text-center"}>
                        <ErrorsDisplay errors={errors} />
                        <form>
                            <div className="row">
                                {props.baseState != null && props.baseState.Intranet_Title && <>
                                    {props.baseState.Intranet_Title_Required && <>
                                        <ValidatedStringFormInput Name={`Title ${props?.baseState?.PersonalDetails_TitleExamples}`} BaseValue={data?.Title} Invalid={(data?.Title ?? "").length == 0} MaxSize={30} ColWidth={4} submitting={submitting} setValueallback={function (val: string): void {
                                            updater(val, 'Title');
                                        }} />
                                    </>}
                                    {!props.baseState.Intranet_Title_Required && <>
                                        <div className={`col-lg-4`}>
                                            <label htmlFor={`control_Title`} className="form-label-alt">Title {props?.baseState?.PersonalDetails_TitleExamples}</label>
                                            <input disabled={submitting} id={`control_Title`} aria-label={"Title"} className="form-control"
                                                value={data?.Title ?? ''}
                                                type="text"
                                                maxLength={30}
                                                onChange={t => {
                                                    updater(t.currentTarget.value, "Title");
                                                }} />
                                        </div>
                                    </>}
                                </>
                                }
                                <ValidatedStringFormInput Name={"First name"} BaseValue={data?.Forenames} Invalid={(data?.Forenames ?? "").length == 0} ColWidth={props.baseState != null &&  !props.baseState.Intranet_Title ? 6 : 4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                    updater(val, "Forenames");
                                }} />
                                <ValidatedStringFormInput Name={"Last name"} BaseValue={data?.Surname} Invalid={(data?.Surname ?? "").length == 0} ColWidth={props.baseState != null &&  !props.baseState.Intranet_Title ? 6 : 4} MaxSize={100} submitting={submitting} setValueallback={function (val: string): void {
                                    updater(val, "Surname");
                                }} />
                                <ValidatedStringFormInput Name={"Email address"} BaseValue={data?.EmailAddress} Invalid={(data?.EmailAddress ?? "").length == 0} MaxSize={100} ColWidth={12} submitting={submitting} setValueallback={function (val: string): void {
                                    updater(val, "EmailAddress");
                                }} />
                                {props.baseState != null && props.baseState.IntranetShortForm_CurrentJobTitle && <>
                                    {props.baseState.Intranet_CurrentJobTitle_Required && <>
                                        <ValidatedStringFormInput Name={"Current job title"} BaseValue={data?.CurrentJobTitle} Invalid={(data?.CurrentJobTitle ?? "").length == 0} MaxSize={255} ColWidth={12} submitting={submitting} setValueallback={function (val: string): void {
                                            updater(val, "CurrentJobTitle");
                                        }} />
                                    </>}
                                    {!props.baseState.Intranet_CurrentJobTitle_Required && <>
                                        <div className={`col-lg-12`}>
                                            <label htmlFor={`control_CurrentJobTitle`} className="form-label-alt">Current job title</label>
                                            <input disabled={submitting} id={`control_CurrentJobTitle`} aria-label={"Current job title"} className="form-control"
                                                value={data?.CurrentJobTitle ?? ''}
                                                type="text"
                                                maxLength={255}
                                                onChange={t => {
                                                    updater(t.currentTarget.value, "CurrentJobTitle");
                                                }} />
                                        </div>
                                    </>}
                                </>}
                                {props.baseState != null && props.baseState.Intranet_CurrentSalary && <>
                                    {props.baseState.Intranet_CurrentSalary_Required && <>
                                        <ValidatedStringFormInput Name={"Current salary"} BaseValue={data?.CurrentSalary} Invalid={(data?.CurrentSalary ?? "").length == 0} MaxSize={50} ColWidth={6} submitting={submitting} setValueallback={function (val: string): void {
                                            updater(val, "CurrentSalary");
                                        }} />
                                    </>}
                                    {!props.baseState.Intranet_CurrentSalary_Required && <>
                                        <div className={`col-lg-6`}>
                                            <label htmlFor={`control_CurrentSalary`} className="form-label-alt">Current salary</label>
                                            <input disabled={submitting} id={`control_CurrentSalary`} aria-label={"Current salary"} className="form-control"
                                                value={data?.CurrentSalary ?? ''}
                                                type="text"
                                                maxLength={50}
                                                onChange={t => {
                                                    updater(t.currentTarget.value, "CurrentSalary");
                                                }} />
                                        </div>
                                    </>}
                                </>}
                                {props.baseState != null && props.baseState.Intranet_DesiredSalary && <>
                                    {props.baseState.Intranet_DesiredSalary_Required && <>
                                        <ValidatedStringFormInput Name={"Desired salary"} BaseValue={data?.DesiredSalary} Invalid={(data?.DesiredSalary ?? "").length == 0} MaxSize={50} ColWidth={6} submitting={submitting} setValueallback={function (val: string): void {
                                            updater(val, "DesiredSalary");
                                        }} />
                                    </>}
                                    {!props.baseState.Intranet_DesiredSalary_Required && <>
                                        <div className={`col-lg-6`}>
                                            <label htmlFor={`control_DesiredSalary`} className="form-label-alt">Desired salary</label>
                                            <input disabled={submitting} id={`control_DesiredSalary`} aria-label={"Desired salary"} className="form-control"
                                                value={data?.DesiredSalary ?? ''}
                                                type="text"
                                                maxLength={50}
                                                onChange={t => {
                                                    updater(t.currentTarget.value, "DesiredSalary");
                                                }} />
                                        </div>
                                    </>}
                                </>}
                                {props.baseState != null && props.baseState.Intranet_PassedProbationPeriod && <>
                                    <div className={`col-lg-6`}>
                                        <label htmlFor={`control_PassedProbationPeriod`} className="form-label-alt">
                                            {props.baseState.Intranet_PassedProbationPeriod_Required && <RequiredIndicator isOk={(data?.PassedProbationPeriod?.length ?? 0) > 0} />} Passed probation period
                                        </label>
                                        <select ref={inputRefPassedProbationPeriod} disabled={submitting} id={`control_PassedProbationPeriod`} onChange={x => {
                                            x.preventDefault();
                                            inputRefPassedProbationPeriod?.current?.classList.remove("is-valid");
                                            inputRefPassedProbationPeriod?.current?.classList.remove("is-invalid");
                                            updater(x.currentTarget.value, "PassedProbationPeriod");
                                        }} value={data?.PassedProbationPeriod} defaultValue={""} className="form-select" aria-label="Passed probation period">
                                            <option value={""} key={"Please_select"}>Please select</option>
                                            <option value={"1"} key={"Jan"}>Yes</option>
                                            <option value={"0"} key={"Feb"}>No</option>
                                        </select>
                                        {props.baseState.Intranet_PassedProbationPeriod_Required && <div className="invalid-feedback">
                                            Passed probation period required
                                        </div>}
                                    </div>
                                </>}
                                <hr className="mt-4" />
                                <div className="col-lg-12 mt-2">
                                    <div>
                                        {cvFile == null && <><label htmlFor="formFileCV" className="form-label-alt"><RequiredIndicator isOk={cvFile != null} /> CV</label>
                                            <input ref={cvFileRef} className="form-control" type="file" id="formFileCV" accept=".doc,.docx,.txt,.pdf" onChange={(e: any) => {
                                                updater(e.target.files[0], 'CV');
                                                updater(e.target.files[0].name, 'CVFileName');
                                            }} />
                                        </>}

                                        {cvFile != null && <div className="input-group"><span className="input-group-text">CV: {data?.CVFileName}</span><button className="btn btn-danger" onClick={(e) => {
                                            e.preventDefault();
                                            updater(null, 'CV');
                                            updater('', 'CVFileName');
                                        }}> Remove</button></div>}
                                        <small className="text-muted">Allowed file types: .doc, .docx, .txt, .pdf</small>
                                    </div>
                                </div>
                                <hr className="mt-4" />

                                {props?.baseState?.Intranet_CoverLetterOption && <>
                                    <div className="col-lg-12 mt-2">
                                        <div>
                                            {coverLetterFile == null && <><label htmlFor="formFileCL" className="form-label-alt">Cover letter</label>
                                                <input ref={coverLetterFileRef} className="form-control" type="file" id="formFileCL" accept=".doc,.docx,.txt,.pdf" onChange={(e: any) => {
                                                    updater(e.target.files[0], 'CoverLetter');
                                                    updater(e.target.files[0].name, 'CoverLetterFileName');
                                                }} />
                                            </>}

                                            {coverLetterFile != null && <div className="input-group"><span className="input-group-text">Cover letter: {data?.CoverLetterFileName}</span><button className="btn btn-danger" onClick={(e) => {
                                                e.preventDefault();
                                                updater(null, 'CoverLetter');
                                                updater('', 'CoverLetterFileName');
                                            }}> Remove</button></div>}
                                            <small className="text-muted">Allowed file types: .doc, .docx, .txt, .pdf</small>
                                        </div>
                                    </div>
                                    <hr className="mt-4" />
                                </>}

                                {props.baseState != null && props.baseState.IntranetShortForm_CurrentManagerAware &&
                                    <div className="col-lg-12 mt-2 d-flex justify-content-center">
                                        <input ref={inputCurrentManagerAware} id="checkboxManagerAware" type="checkbox" className="form-check-input me-1" disabled={submitting} checked={data?.CurrentManagerAware} onChange={e => {
                                            updater(e.currentTarget.checked, "CurrentManagerAware");
                                            if (e.currentTarget.checked) {
                                                inputCurrentManagerAware?.current?.classList.remove("is-invalid");
                                                inputCurrentManagerAware?.current?.classList.add("is-valid");
                                            }
                                            else {
                                                inputCurrentManagerAware?.current?.classList.add("is-invalid");
                                                inputCurrentManagerAware?.current?.classList.remove("is-valid");
                                            }
                                        }} />
                                        <label htmlFor="checkboxManagerAware" className="form-check-label no-highlight-clicker me-1">{props.baseState.Intranet_CurrentManagerAware_Label}</label>
                                    </div>
                                }
                            </div>
                            <div className="row">
                                <div className="col-lg-12 text-center">
                                    <button className="btn btn-alt mt-4 btn-wider" disabled={submitting} type="submit" onClick={e => {
                                        e.preventDefault();
                                        saveDetails();
                                    }}>Apply</button>
                                </div>
                            </div>
                        </form>
                    </LoadingContent>
                }
                {submitted && <div className="alert alert-success">You have now completed the application form</div>}
            </Card >
            <RequiredHelper />
        </>
    );
}